import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getInspectionPreferredDates } from 'accounts/utils/home-inspection';
import { utcToZonedTime } from 'date-fns-tz';
import { fetchAvailabilityDates } from 'store/redux/homeowner-set-up-flow/actions';
/** One bookable time slot on a given day, as the picker consumes it. */
type AvailableTime = {
  value: string;
  key: string;
  originalDateTime: string;
};

/** Available slots keyed by `YYYY-MM-DD`. */
type AvailabilityDates = Record<string, AvailableTime[]>;

export function useInspectionAvailabilityDates({
  unit,
  startDate,
  endDate,
  timeZone,
}: {
  unit: any;
  startDate?: Date;
  endDate?: Date;
  timeZone: string;
}) {
  const [dates, setDates] = useState<AvailabilityDates | null>(null);
  const [disabledDates, setDisabledDates] = useState<
    (Date | { before: Date })[]
  >([]);
  const [preferredDates, setPreferredDates] = useState<Date[] | null>(null);
  const [loading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchDates = async () => {
      setIsLoading(true);
      try {
        // The promise middleware resolves `dispatch` to the API response — a list
        // of available date-times — while the `Dispatch` type models it as
        // returning the action object it was handed.
        const response = (await dispatch(
          fetchAvailabilityDates(
            unit?.basicInfo?.id,
            startDate?.toISOString().split('T')[0],
            endDate?.toISOString().split('T')[0],
          ),
        )) as unknown as string[];

        const map: AvailabilityDates = {};

        response.forEach((d) => {
          const date = utcToZonedTime(new Date(d), timeZone);
          // `split` always yields at least one element, so index 0 is present;
          // `noUncheckedIndexedAccess` cannot see that.
          const dateString = date.toISOString().split('T')[0]!;

          const hours = date.getHours();
          const minutes = date.getMinutes();
          const ampm = hours >= 12 ? 'PM' : 'AM';
          const displayHours = hours % 12 || 12;
          const availableTime = `${displayHours}:${minutes < 10 ? `0${minutes}` : minutes} ${ampm}`;

          const hours24 = date.getHours();
          const minutes24 = date.getMinutes();
          const hoursPadded = hours24 < 10 ? `0${hours24}` : `${hours24}`;
          const minutesPadded =
            minutes24 < 10 ? `0${minutes24}` : `${minutes24}`;

          const completeTime = {
            value: availableTime,
            key: `${hoursPadded}:${minutesPadded}`,
            originalDateTime: d,
          };

          if (map[dateString]) {
            map[dateString] = [...map[dateString], completeTime];
          } else {
            map[dateString] = [completeTime];
          }
        });

        setDates(map);
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    if (unit?.basicInfo?.id && startDate && endDate) fetchDates();
  }, [
    unit?.basicInfo?.id,
    startDate?.toISOString(),
    endDate?.toISOString(),
    timeZone,
  ]);

  useEffect(() => {
    if (!dates || !startDate || !endDate) return;

    const end = new Date(endDate);

    const disabled: (Date | { before: Date })[] = [
      {
        before: new Date(startDate),
      },
    ];

    const dayCount =
      new Date(endDate).getDate() - new Date(startDate).getDate();

    for (let i = dayCount; i >= 0; i--) {
      const date = new Date(
        end.getFullYear(),
        end.getMonth(),
        end.getDate() - i,
      );

      // As above: `split` always yields a first element.
      const availability = dates[date.toISOString().split('T')[0]!];
      if (!availability) {
        disabled.push(date);
      }
    }

    setDisabledDates(disabled);
  }, [dates, startDate?.toISOString(), endDate?.toISOString()]);

  useEffect(() => {
    if (!dates) return;

    const preferredDays = getInspectionPreferredDates({
      availableDates: dates,
      timeZone,
    });

    setPreferredDates(preferredDays);
  }, [dates, timeZone]);

  return {
    availabilityDates: dates,
    disabledDates,
    preferredDates,
    isLoadingDates: loading,
  };
}
